import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Hot water extraction: living room, three bedrooms and upstairs hall', amount: '$260' },
  { item: 'Pet urine treatment with enzyme pre-spray, two bedrooms', amount: '$80' },
  { item: 'Sofa and loveseat, microfiber, upholstery clean', amount: '$190' },
  { item: 'Optional: carpet protector on the living room traffic lane', amount: '$45' },
];

const faq = [
  {
    q: 'Will every stain come out?',
    a: 'No, and the quote should say so. Old pet stains, bleach spots, dye transfer and wear in traffic lanes may lighten without disappearing. Writing that into your terms means the customer agreed to it before you started.',
  },
  {
    q: 'How do carpet cleaners usually price?',
    a: 'By room or by square foot for the carpet, with separate prices for stairs, pet treatment, protector, upholstery and area rugs. Coupon-style whole-house prices tend to cause arguments when extras appear at the door.',
  },
  {
    q: 'Can customers add protector or a rug on the quote?',
    a: 'Yes. Put extras on the quote as optional lines. The customer ticks what they want when they open it, and the total changes on the page before they sign.',
  },
  {
    q: 'Can customers book a cleaning online in Scopie?',
    a: 'No. Scopie does not have online booking or self-scheduling. You send the quote, the customer signs it, and you put the job on your schedule.',
  },
];

const page: IndustryPage = {
  slug: 'carpet-cleaning',
  order: 13,
  group: 'Cleaning',
  path: '/for/carpet-cleaning',
  primaryKeyword: 'carpet cleaning business software',
  secondaryKeywords: ['carpet cleaning quote template', 'how to price carpet cleaning', 'upholstery cleaning quote'],
  title: 'Carpet Cleaning Software for Room-by-Room Quotes | Scopie', // 57
  metaDescription:
    'Quote carpet and upholstery cleaning by room, with pet treatment, stairs and rugs on their own lines and protector as an extra customers can tick.', // 147
  h1: 'Carpet and upholstery quotes the customer agrees to before you arrive',
  answerFirst:
    'Scopie gives carpet cleaners a quote that lists rooms, stairs, pet treatment and upholstery separately, with protector as an extra the customer can tick. Your wording on old stains, drying time and furniture moving is on the page they sign, so there is no debate at the door and no surprise on the invoice.',
  exampleQuote: { label: 'Sample carpet cleaning quote', lines, total: '$575', depositNote: 'Stain and drying-time wording is the cleaner\'s own clause.' },
  blocks: [
    {
      type: 'callout',
      id: 'door',
      text: 'The hardest conversation in carpet cleaning happens at the front door, when the coupon price meets the stairs and the dog. Have it on the quote instead.',
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'Five areas, a pet problem and a sofa',
      label: 'Sample quote: whole-floor clean with pet treatment',
      lines,
      total: '$575',
      totalLabel: 'Total with protector',
      note: 'Example prices. Protector is optional, ticked or skipped by the customer on the quote page.',
    },
    {
      type: 'list',
      id: 'terms',
      h2: 'What your terms should say about results',
      style: 'checklist',
      items: [
        { text: 'Which stains may lighten but not disappear: urine, bleach, dye, rust, coffee set for years.' },
        { text: 'How long carpets take to dry and when furniture can go back.' },
        { text: 'What the crew moves, and what the customer should clear: breakables, cords, small items.' },
        { text: 'Whether you clean under beds and heavy furniture.' },
        { text: 'What happens if a room cannot be reached on the day.' },
      ],
    },
    {
      type: 'text',
      id: 'truck-mount',
      h2: 'One truck-mount, two nine o\'clock jobs',
      paragraphs: [
        'Signed jobs go on the schedule with the technician and the machine, truck-mount van or portable extractor, so it is clear which van is where. Technicians see the address and time but not the price, and the invoice is a link to share once the hoses are rolled up.',
        'Customers come back every year or so, more often with pets. Each one stays in your client list with the rooms and extras from last time.',
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Carpet cleaning quote mistakes',
      style: 'mistakes',
      items: [
        { title: 'Whole-house coupon pricing', text: 'It sets the customer up for a surprise. Price rooms and extras.' },
        { title: 'Stairs counted as a room', text: 'A flight of stairs takes as long as a bedroom. Price it per step or per flight.' },
        { title: 'Pet treatment offered at the door', text: 'It sounds like an upsell then. On the quote it sounds like a plan.' },
        { title: 'No access rule', text: 'Parking for the van and hose length to the far bedroom matter. Note them.' },
        { title: 'Rugs cleaned in place without a note', text: 'Some rugs bleed or shrink. Say when a rug goes to the plant instead.' },
      ],
    },
    {
      type: 'list',
      id: 'calendar',
      h2: 'When the calls come in',
      style: 'bullets',
      items: [
        { title: 'Before the holidays', text: 'Hosts want the living room and the guest room fresh by mid-November, so book those quotes early.' },
        { title: 'Spring', text: 'Mud season and shedding pets bring the second wave, often with upholstery added.' },
        { title: 'Move-outs', text: 'Landlords and tenants need a receipt-ready invoice the same week the keys are returned.' },
        { title: 'After a leak', text: 'Water-damaged carpet may need a restoration company first; say on the quote when a job is outside a normal clean.' },
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Carpet cleaning: questions from customers', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/cleaning', anchor: 'Scopie for cleaning businesses', status: 'live' },
    { href: '/for/window-cleaning', anchor: 'Scopie for window cleaning businesses', status: 'live' },
    { href: '/templates/carpet-cleaning-quote-template', anchor: 'free carpet cleaning quote template', status: 'planned' },
  ],
  cta: {
    heading: 'Settle it on the quote, not at the door',
    text: 'Put your room prices, treatments and stain wording into Scopie once and reuse them on every job.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
